<template>
  <!-- #830 · el bloque se plega con su triangulito, como el resto de los grupos del Centro de
       Cobros: era el único que no, porque entró con la #199 después de la #749. Igual que en
       `CuadroComportamientoPago`, el estado NO vive aquí: lo manda la pantalla por `abierto` y se le
       avisa con `alternar`, porque los bloques de esa pantalla se recuerdan todos en la misma
       entrada del navegador y no puede haber dos dueños escribiendo en ella.

       Sin `abierto`, el bloque nace abierto y se comporta como antes de esto. -->
  <bloque-plegable titulo="La gestión" :nota="notaTitulo"
    :abierto="abierto" :resumen="resumenCerrado" :refrescando="cargando"
    :aria-busy="cargando ? 'true' : 'false'" @alternar="$emit('alternar')">

    <div v-if="cargando && !dto" class="cuadro-cargando text-align-center">
      <f7-preloader></f7-preloader>
    </div>

    <!-- El aviso de fallo, con su reintento al lado de la frase que lo anuncia. Un 403 no se
         reintenta: con él no hay nada que pulsar. -->
    <div v-if="error" class="aviso-error">
      <span>{{ error }}</span>
      <button v-if="reintentable" type="button" class="boton-reintentar" @click="cargar">
        Reintentar
      </button>
    </div>

    <template v-if="dto">

      <!-- a) FACTURAS SIN NINGUNA GESTIÓN · las dos cifras de D-COB-4: el total y, dentro, lo ya
           vencido. Las dos con su importe, que es como se cotejan contra el listado del día. -->
      <p class="bloque-subtitulo">Facturas sin ninguna gestión</p>
      <div class="cifras">
        <div class="cifra">
          <span class="cifra-valor">{{ numero(sinGestion.facturas) }}</span>
          <span class="cifra-rotulo">facturas · {{ formatEurosConUnidad(sinGestion.importe) }}</span>
        </div>
        <div class="cifra">
          <span class="cifra-valor">{{ numero(sinGestion.facturasVencidas) }}</span>
          <span class="cifra-rotulo">de ellas ya vencidas ·
            {{ formatEurosConUnidad(sinGestion.importeVencido) }}</span>
        </div>
      </div>
      <!-- D-COB-8 · lo que cuenta sin poder medirse se dice, no se esconde dentro de la cifra. -->
      <p class="nota-dato nota-dato-alerta" v-if="sinGestion.facturasSinVencimiento || sinGestion.facturasSinSaldo">
        <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
        <span>
          <template v-if="sinGestion.facturasSinVencimiento">
            {{ numero(sinGestion.facturasSinVencimiento) }} no tienen fecha de vencimiento: cuentan
            como sin gestión y no entran en «ya vencidas».
          </template>
          <template v-if="sinGestion.facturasSinSaldo">
            {{ numero(sinGestion.facturasSinSaldo) }} llegan sin saldo pendiente: cuentan como
            factura y no aportan euros<template v-if="sinGestion.facturasVencidasSinSaldo">
            ({{ numero(sinGestion.facturasVencidasSinSaldo) }} de ellas, entre las ya
            vencidas)</template>.
          </template>
        </span>
      </p>
      <p class="nota-dato nota-dato-pie">{{ sinGestion.nota }}</p>

      <!-- b) SILENCIOS · el N va en la cabecera del propio panel: no hay una pantalla de Ajustes
           del Centro de Cobros donde encaje, y dirección pidió no inventar una (#199). -->
      <p class="bloque-subtitulo bloque-subtitulo-separado">Silencios</p>
      <div class="silencio-n">
        <span>
          Facturas vencidas cuya última gestión es de hace más de
          <b>{{ silencios.dias }} días</b>.
          <span class="bloque-nota">{{ silencios.origenDias }}</span>
        </span>
        <!-- El campo solo se dibuja para quien puede cambiarlo (D-COB-5): N decide lo que se le
             enseña a todos. A quien no puede se le dice quién lo cambia, en vez de un campo mudo. -->
        <template v-if="silencios.puedeCambiarDias">
          <label class="silencio-campo">
            <span class="periodo-rotulo">Cambiar a</span>
            <input type="number" min="1" max="365" step="1" v-model.number="nuevoN"
              :disabled="guardandoN" @input="nEditado = true" @keyup.enter="guardarN" />
            <span>días</span>
          </label>
          <button type="button" class="boton-gestiones" :disabled="guardandoN || !nValido"
            @click="guardarN">
            {{ guardandoN ? 'Guardando…' : 'Guardar' }}
          </button>
          <!-- Por qué no se puede guardar, junto al botón deshabilitado (regla de dirección del
               05/10/2026): un botón gris sin explicación es un botón mudo. -->
          <span class="silencio-aviso" v-if="!guardandoN && nuevoN !== '' && nuevoN !== null && !nValido">
            Tiene que ser un número entero de días entre 1 y 365.
          </span>
          <span class="silencio-aviso" v-if="errorN">{{ errorN }}</span>
        </template>
        <span v-else class="bloque-nota">
          Lo cambian Administración, Directores y Administrador, desde este mismo panel.
        </span>
      </div>
      <p class="bloque-vacio" v-if="cargando">
        <f7-preloader size="14"></f7-preloader> Recalculando la lista…
      </p>
      <p class="silencio-total">
        {{ numero(silencios.facturas) }} facturas · {{ formatEurosConUnidad(silencios.importe) }}
      </p>
      <!-- D-COB-8 · junto al total que se queda corto, no solo en la celda de la fila. -->
      <p class="nota-dato nota-dato-alerta" v-if="silencios.facturasSinSaldo">
        <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
        <span>
          {{ numero(silencios.facturasSinSaldo) }} llegan sin saldo pendiente: están en la lista y
          no aportan euros al total, que se queda corto en ellas.
        </span>
      </p>
      <div class="tabla-scroll" v-if="silencios.lista && silencios.lista.length">
        <table class="tabla">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Factura</th>
              <th>Vencimiento</th>
              <th class="num">Días vencida</th>
              <th class="num">Saldo pendiente</th>
              <th>Última gestión</th>
              <th class="num">Días de silencio</th>
              <th>Gestor de cobro (PGC)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="fila in silencios.lista" :key="fila.id">
              <td>{{ texto(fila.cliente) }}</td>
              <td>{{ texto(fila.codigo || fila.numero) }}</td>
              <td>{{ formatFecha(fila.fechaVencimiento) }}</td>
              <td class="num">{{ numero(fila.diasRetraso) }}</td>
              <td class="num" :class="celdaFalta(fila.saldoPendiente)">
                {{ formatEurosConUnidad(fila.saldoPendiente) }}
              </td>
              <td>{{ formatFecha(fila.fechaUltimaGestion) }}</td>
              <td class="num"><b>{{ numero(fila.diasSilencio) }}</b></td>
              <td :class="celdaFalta(fila.gestorCobro)">{{ fila.gestorCobro || 'Sin asignar' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="bloque-vacio" v-else>Ninguna factura vencida lleva más de {{ silencios.dias }} días sin gestión.</p>
      <p class="nota-dato nota-dato-pie">{{ silencios.nota }}</p>

      <!-- c) ACTIVIDAD POR GESTOR Y SEMANA · quien hizo la gestión, no el PGC de la factura. -->
      <p class="bloque-subtitulo bloque-subtitulo-separado">Actividad por gestor y semana</p>
      <div class="periodo">
        <label class="periodo-campo">
          <span class="periodo-rotulo">Semanas</span>
          <select v-model.number="semanas" @change="cargar">
            <option v-for="opcion in OPCIONES_SEMANAS" :key="opcion" :value="opcion">
              Últimas {{ opcion }}
            </option>
          </select>
        </label>
        <span class="periodo-aplicado">
          Del {{ formatFecha(dto.actividadDesde) }} al {{ formatFecha(dto.actividadHasta) }},
          con la semana en curso hasta ahora<template v-if="dto.semanas"> (últimas {{ dto.semanas }})</template>.
        </span>
        <!-- Lo apuntado para más tarde no es actividad todavía: se dice cuánto hay, porque es la
             diferencia con la consulta de Tareas filtrada por la semana entera. -->
        <span class="periodo-aplicado" v-if="dto.gestionesPlanificadas">
          {{ numero(dto.gestionesPlanificadas) }}
          {{ dto.gestionesPlanificadas === 1 ? 'gestión apuntada' : 'gestiones apuntadas' }} para
          más tarde esta semana: no cuentan hasta que llegue su hora.
        </span>
        <!-- Mientras llega la respuesta la tabla sigue siendo la del período de arriba, y el
             selector ya dice el nuevo: se avisa aquí, al lado del selector, de que no coinciden. -->
        <span class="periodo-aplicado" v-if="cargando && dto.semanas !== semanas">
          <f7-preloader size="14"></f7-preloader>
          Cargando las últimas {{ semanas }}: hasta que lleguen, la tabla sigue siendo la de las
          últimas {{ dto.semanas }}.
        </span>
      </div>
      <div class="tabla-scroll" :class="{ 'tabla-recalculando': cargando }" v-if="actividad.length">
        <table class="tabla">
          <thead>
            <tr>
              <th>Semana</th>
              <th>Gestor</th>
              <th class="num">Gestiones</th>
              <th class="num">Tiempo dedicado</th>
              <th class="num">Sin hora de fin</th>
              <th class="num">Tiempo estimado</th>
              <th class="num">Documentos tocados</th>
              <th class="num">Cartera tocada</th>
              <th class="num">Cobrado tras gestión</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="fila in actividad" :key="fila.semanaDesde + '|' + (fila.empleado || '')">
              <td>{{ formatFecha(fila.semanaDesde) }} – {{ formatFecha(fila.semanaHasta) }}</td>
              <td :class="celdaFalta(fila.empleado)">{{ fila.etiqueta }}</td>
              <td class="num">{{ numero(fila.gestiones) }}</td>
              <td class="num" :class="celdaFalta(fila.minutosDedicados)">
                {{ formatMinutos(fila.minutosDedicados) }}
              </td>
              <td class="num">{{ numero(fila.gestionesSinTiempo) }}</td>
              <td class="num" :class="celdaFalta(fila.minutosEstimados)">
                {{ formatMinutos(fila.minutosEstimados) }}
              </td>
              <td class="num">{{ numero(fila.documentosTocados) }}</td>
              <td class="num">
                {{ formatEurosConUnidad(fila.importeCarteraTocada) }}
                <span class="celda-falta" v-if="fila.documentosTocadosSinSaldo"
                  title="No aportan euros: llegan sin saldo pendiente">
                  · {{ numero(fila.documentosTocadosSinSaldo) }} sin saldo
                </span>
              </td>
              <!-- D-COB-7 · a raya con el motivo escrito debajo, nunca a cero. -->
              <td class="num celda-falta" :title="dto.motivoCobradoTrasGestion">
                {{ formatEurosConUnidad(fila.cobradoTrasGestion) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="bloque-vacio" v-else>No hay ninguna gestión de cobro fechada en estas semanas.</p>
      <p class="nota-dato nota-dato-alerta">
        <f7-icon f7="exclamationmark_triangle" class="nota-dato-icono-alerta"></f7-icon>
        <span>{{ dto.motivoCobradoTrasGestion }}</span>
      </p>
      <p class="nota-dato nota-dato-pie">{{ dto.notaTiempo }}</p>
      <p class="nota-dato nota-dato-pie">{{ dto.notaActividad }}</p>
    </template>
  </bloque-plegable>
</template>

<script>
import FactCobro from '../js/db/FactCobro.js';
import BloquePlegable from './BloquePlegable.vue';
import carrilPeticiones from '../js/utils/carrilPeticiones.js';
import cerrojoDeLaSesion from '../js/utils/cerrojoDeLaSesion.js';

/** Las ventanas que ofrece la tabla de actividad. La API admite hasta 53. */
const OPCIONES_SEMANAS = [4, 8, 12, 26, 52];

/** Las que pide la API cuando no se le dice nada. */
const SEMANAS_POR_OMISION = 12;

/**
 * La vista **«La gestión»** del Centro de Cobros (COB-5a, #199): las tareas de gestión de cobro
 * sacadas de dentro de cada ficha y sumadas en tres paneles.
 *
 * **De solo lectura**, salvo el N de los silencios: ni crea tareas, ni cambia estados de gestión,
 * ni toca ningún campo de la factura.
 *
 * Es un componente con su propia lectura por lo mismo que `CuadroComportamientoPago`: necesita las
 * tareas de las últimas semanas por fecha, que es otra consulta, y así la pantalla que ya funciona
 * no se reorganiza para hacerle sitio. En `centro-cobros.vue` el diff es la línea que lo monta y la
 * que lo recarga con el botón de la cabecera.
 *
 * Los textos de las notas los manda la API: son las decisiones de dirección sobre el dato.
 */
export default {
  name: 'CuadroGestionCobro',
  components: { BloquePlegable },
  props: {
    /**
     * #830 · Si el bloque está desplegado. Lo decide y lo recuerda la pantalla que lo monta, que es
     * quien tiene el resto de los bloques del Centro de Cobros.
     *
     * **Por omisión abierto**, por lo mismo que en `CuadroComportamientoPago`: si la pantalla
     * dejara de pasarlo, el cuadro se ve como siempre, y no escondido sin que nadie lo haya plegado.
     */
    abierto: { type: Boolean, default: true }
  },
  data: function() {
    return {
      cargando: false,
      error: null,
      reintentable: false,
      dto: null,
      semanas: SEMANAS_POR_OMISION,
      nuevoN: null,
      nEditado: false,
      guardandoN: false,
      errorN: null,
      dudaN: null,
      OPCIONES_SEMANAS: OPCIONES_SEMANAS
    };
  },
  computed: {
    sinGestion: function() {
      return (this.dto && this.dto.sinGestion) || {};
    },
    silencios: function() {
      return (this.dto && this.dto.silencios) || {};
    },
    actividad: function() {
      return (this.dto && this.dto.actividad) || [];
    },
    /** La coletilla del rótulo, que es la que ya tenía antes del triángulo. */
    notaTitulo: function() {
      const tipo = this.dto && this.dto.tipoTarea;
      return '· toda la cartera que ves, sin los filtros de abajo'
        + (tipo ? ' · tareas de tipo ' + tipo : '');
    },
    /**
     * #830 · Lo esencial del bloque para cuando está plegado: las facturas sin ninguna gestión y
     * los silencios, cada uno con su número de facturas y su importe.
     *
     * Con los **mismos campos** que pinta el bloque por dentro, y el N de los silencios es el que
     * ha aplicado el servidor (`silencios.dias`), no el que se esté tecleando en el campo: es el que
     * dice la frase de dentro, y cerrado y abierto no pueden contar silencios distintos.
     *
     * Sin respuesta no inventa cifras: si la lectura ha fallado dice que ha fallado, que es lo que
     * se leería abierto; mientras llega la primera, el «Actualizando…» de la cabecera ya lo dice.
     */
    resumenCerrado: function() {
      if (!this.dto) return this.error || '';
      const sinGestion = this.sinGestion;
      const silencios = this.silencios;
      const deMasDe = this.falta(silencios.dias) ? '' : ' de más de ' + silencios.dias + ' días';
      return 'Sin ninguna gestión: ' + this.numero(sinGestion.facturas) + ' facturas · '
        + this.formatEurosConUnidad(sinGestion.importe)
        + ' · Silencios' + deMasDe + ': ' + this.numero(silencios.facturas) + ' facturas · '
        + this.formatEurosConUnidad(silencios.importe);
    },
    nValido: function() {
      const n = this.nuevoN;
      return Number.isInteger(n) && n >= 1 && n <= 365;
    }
  },
  created: function() {
    this.carril = carrilPeticiones();
  },
  mounted: function() {
    this.cargar();
  },
  methods: {
    /**
     * Pide la vista. Es público: lo llama también el botón de recargar de la cabecera de
     * `centro-cobros.vue`, por su `ref`. Por el carril de peticiones, porque cambiar de semanas
     * dos veces seguidas lanza dos y las respuestas no vuelven en orden.
     */
    cargar: function() {
      const self = this;
      // #663 · Cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla la respuesta
      // que lo soltaba y el componente lo conservaría puesto en la cuenta siguiente.
      cerrojoDeLaSesion(self, 'cargando');
      self.error = null;
      self.reintentable = false;
      self.carril.lanzar(FactCobro.gestionCartera(self.semanas), {
        exito: function(respuesta) {
          self.cargando = false;
          self.dto = respuesta;
          // El campo arranca en el N vigente: así «Guardar» sin tocarlo no cambia nada. Pero solo
          // mientras nadie lo haya editado: cambiar las semanas o recargar desde la cabecera vuelve
          // a pasar por aquí, y pisar el número tecleado lo borraría sin avisar antes de guardarlo.
          if (respuesta && respuesta.silencios && !self.nEditado) {
            self.nuevoN = respuesta.silencios.dias;
          }
          // Un guardado sin respuesta se resuelve aquí, en la lectura, y no en su `catch`: así lo
          // resuelve igual la relectura de después del fallo, cambiar las semanas o la recarga de la
          // cabecera. Si consta el número que se mandó, entró, y la frase de la duda deja de
          // pintarse; si no, se queda, y volver a guardar es seguro porque el servidor no deja que
          // el de antes, si llega tarde, pise al nuevo.
          if (self.dudaN !== null && respuesta && respuesta.silencios
              && respuesta.silencios.dias === self.dudaN) {
            self.dudaN = null;
            self.errorN = null;
            self.nEditado = false;
            self.nuevoN = respuesta.silencios.dias;
          }
        },
        limpiar: function() {
          self.cargando = false;
          self.dto = null;
        },
        fallo: function(e) {
          self.cargando = false;
          const sinPermiso = !!(e && e.status === 403);
          self.reintentable = !sinPermiso;
          self.error = sinPermiso
            ? 'No tienes acceso a «La gestión». Lo abre Administración, desde los permisos del '
              + 'Centro de Cobros.'
            : 'No se ha podido cargar «La gestión».';
        }
      });
    },
    /**
     * Guarda el N y vuelve a pedir la vista: lo que se enseña después es lo que el servidor ha
     * releído, no lo que se tecleó. Cambiar N solo mueve la lista de silencios: los otros dos
     * paneles no lo leen.
     *
     * Manda la versión de lo guardado que se leyó: si entretanto ha cambiado —otra persona, otra
     * pestaña o un guardado anterior que llega tarde—, el servidor no escribe y dice qué consta.
     */
    guardarN: function() {
      const self = this;
      if (!self.nValido || self.guardandoN) return;
      const dias = self.nuevoN;
      cerrojoDeLaSesion(self, 'guardandoN');
      self.errorN = null;
      FactCobro.cambiarDiasDeSilencio(dias, self.silencios.versionDias).then(function() {
        self.guardandoN = false;
        self.dudaN = null;
        // Guardado: el campo vuelve a seguir al servidor, que es quien dice lo que ha quedado.
        self.nEditado = false;
        self.cargar();
      }).catch(function(e) {
        self.guardandoN = false;
        if (e && e.respuestaPerdida) {
          // No se sabe si ha entrado: ni «no se ha guardado» ni el mensaje genérico de recargar.
          self.dudaN = dias;
          self.errorN = 'No ha llegado la respuesta, así que no se sabe si se han guardado los '
            + dias + ' días. Se vuelve a leer lo que consta: si arriba no pone ' + dias
            + ', puedes guardarlo otra vez sin riesgo, porque el guardado anterior ya no puede '
            + 'pisar al nuevo aunque llegue tarde.';
        } else {
          self.dudaN = null;
          self.errorN = (e && e.message)
            || 'No se han podido guardar los días de silencio. Vuelve a intentarlo.';
        }
        // Se relee siempre: la versión con la que se guarde la próxima vez tiene que ser la que
        // consta ahora, y arriba tiene que poner el N vigente. El número tecleado se conserva.
        self.cargar();
      });
    },
    falta: function(valor) {
      return valor === null || valor === undefined || valor === '';
    },
    celdaFalta: function(valor) {
      return { 'celda-falta': this.falta(valor) };
    },
    /** Una fecha del servidor (`2026-10-02`) como `02/10/2026`, sin pasar por `new Date()`. */
    formatFecha: function(valor) {
      if (!valor) return '—';
      const partes = String(valor).substring(0, 10).split('-');
      if (partes.length !== 3) return String(valor);
      return partes[2] + '/' + partes[1] + '/' + partes[0];
    },
    /** Un importe con su unidad, o un guion sin unidad cuando no hay dato (D-COB-8). */
    formatEurosConUnidad: function(valor) {
      if (this.falta(valor)) return '—';
      return Math.round(Number(valor)).toLocaleString('es-ES') + ' €';
    },
    numero: function(valor) {
      if (this.falta(valor)) return '—';
      return Number(valor).toLocaleString('es-ES');
    },
    /** Minutos como `3 h 05 min`; sin dato, un guion: no hay tiempo medido, no hay cero minutos. */
    formatMinutos: function(valor) {
      if (this.falta(valor)) return '—';
      const total = Math.round(Number(valor));
      const horas = Math.floor(total / 60);
      const minutos = total % 60;
      return (horas ? horas + ' h ' : '') + (horas && minutos < 10 ? '0' : '') + minutos + ' min';
    },
    texto: function(valor) {
      return this.falta(valor) ? '—' : String(valor);
    }
  }
};
</script>

<style scoped>
/* Los estilos son los del Centro de Cobros, repetidos aquí porque el `scoped` del padre no llega a
   un componente hijo; mismo criterio que CuadroComportamientoPago. */
/* La caja y el rótulo del bloque los pinta ahora `BloquePlegable`, que es quien lleva el triángulo
   (#830): con los dos declarados también aquí habría dos cajas, una dentro de la otra. */
.bloque-subtitulo { font-size: 12px; font-weight: 700; color: #4a5a78; margin: 12px 0 6px; }
.bloque-subtitulo-separado { margin-top: 20px; }
.bloque-nota { font-size: 11px; font-weight: 400; color: #7c8db0; margin: 6px 0 0; }
.bloque-vacio { font-size: 12px; color: #7c8db0; margin: 8px 0 0; }
.cuadro-cargando { padding: 12px 0; }

.cifras { display: flex; flex-wrap: wrap; gap: 12px; }
.cifra {
  display: flex;
  flex-direction: column;
  background: #F7F9FC;
  border-radius: 8px;
  padding: 8px 12px;
  min-width: 180px;
}
.cifra-valor { font-size: 20px; font-weight: 700; color: #1a2744; }
.cifra-rotulo { font-size: 12px; color: #4a5a78; }

.nota-dato {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 8px 0 0;
  font-size: 12px;
  color: #4a5a78;
  line-height: 1.4;
  background: #F2F7FD;
  border-radius: 8px;
  padding: 8px 10px;
}
.nota-dato-alerta { background: #FDF4E3; color: #6B4A10; }
.nota-dato-icono-alerta { font-size: 16px; color: #9A6411; flex-shrink: 0; }
.nota-dato-pie { margin: 8px 0 0; }

.silencio-n {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  font-size: 12px;
  color: #1a2744;
}
.silencio-campo { display: inline-flex; align-items: center; gap: 6px; }
.silencio-campo input {
  width: 70px;
  border: 1px solid #c8d3e5;
  border-radius: 6px;
  padding: 3px 6px;
  font-size: 12px;
}
.silencio-aviso { font-size: 12px; color: #8E2B20; }
.silencio-total { font-size: 12px; font-weight: 700; color: #1a2744; margin: 8px 0 4px; }

.periodo { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; margin: 0 0 6px; }
.periodo-campo { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; }
.periodo-rotulo { font-size: 11px; color: #7c8db0; }
.periodo-aplicado { font-size: 11px; color: #7c8db0; }

.tabla-scroll { overflow-x: auto; }
.tabla-recalculando { opacity: 0.5; }
.tabla { width: 100%; border-collapse: collapse; font-size: 12px; }
.tabla th {
  text-align: left;
  font-weight: 600;
  color: #4a5a78;
  border-bottom: 1px solid #e0e0e0;
  padding: 6px 8px;
  white-space: nowrap;
}
.tabla td { border-bottom: 1px solid #f0f0f0; padding: 5px 8px; color: #1a2744; }
.tabla .num { text-align: right; white-space: nowrap; }
.celda-falta { color: #9aa5ba; font-style: italic; }

.aviso-error {
  background: #FDECEA;
  border-radius: 8px;
  color: #8E2B20;
  font-size: 13px;
  padding: 8px 10px;
  margin: 8px 0 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.boton-reintentar,
.boton-gestiones {
  font-size: 12px;
  padding: 4px 12px;
  border: 1px solid #c8d3e5;
  border-radius: 6px;
  background: white;
  color: #185FA5;
  cursor: pointer;
}
.boton-gestiones:disabled { color: #9aa5ba; cursor: default; }
</style>
